import {useState} from 'react';
import styled, {css, keyframes} from 'styled-components';
import {OlliePlush} from '../mascot/OlliePlush';
import {FELT, FeltDefs, useFeltIds} from './felt';
import {LuckyDie, OLLIE, OllieHead, Sparkle} from './feltFriends';
import {Hop, ToyShell, type MapToyProps} from './mapToys';
import {playTones, type Tone} from './toySounds';

/**
 * Ollie map toys, each built on something real otters do: floating on
 * their backs with a treasure on their tummies, holding paws while they
 * nap so they don't drift apart, and juggling pebbles. Like the other map
 * toys they carry no visible text; {@link ToyShell} names them and
 * announces each change.
 */

const WATER = '#8CC4E0';
const WATER_LIGHT = '#B9DCEC';

const reducedMotion = css`
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/** The water's surface, drawn over a floating otter's lower half. */
function WaterFront({y}: {y: number}) {
  return (
    <path
      d={`M 2 ${y} q 6 -2.4 12 0 t 12 0 t 12 0 t 12 0 t 12 0 t 12 0 t 12 0 t 12 0 V 64 H 2 Z`}
      fill={WATER}
      opacity={0.92}
    />
  );
}

/* ── Floating Ollie ───────────────────────────────────────────────────── */

const toss = keyframes`
  0% { transform: translateY(0) rotate(0deg); }
  45% { transform: translateY(-18px) rotate(200deg); }
  100% { transform: translateY(0) rotate(360deg); }
`;

/** The die's flight: up off Ollie's tummy, a spin, and back down. */
const Toss = styled.g`
  transform-box: fill-box;
  transform-origin: center;
  animation: ${toss} 0.75s ease-in-out 1;
  ${reducedMotion}
`;

const DIE_TICKS: readonly Tone[] = [1568, 1318.5, 1760].map(
  (frequency, index) => ({
    frequency,
    startSec: index * 0.09,
    durationSec: 0.06,
  }),
);

const SIX_CHIME: readonly Tone[] = [784, 987.8, 1174.7, 1568].map(
  (frequency, index) => ({
    frequency,
    startSec: 0.7 + index * 0.08,
    durationSec: 0.3,
  }),
);

/**
 * Ollie floating on their back, as otters do, with the lucky die on their
 * tummy. Each press tosses the die and catches it again; a six makes Ollie
 * very happy.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToyOllieFloat({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const [tosses, setTosses] = useState(0);
  const [face, setFace] = useState(3);
  const isSix = tosses > 0 && face === 6;

  return (
    <ToyShell
      isPreview={isPreview}
      label="Ollie floating"
      caption={
        tosses === 0
          ? 'Ollie floats on their back with the lucky die'
          : isSix
            ? 'Ollie tosses the lucky die: a six! Ollie is delighted'
            : `Ollie tosses the lucky die: ${face}`
      }
      onPoke={() => {
        const next = 1 + Math.floor(Math.random() * 6);
        setFace(next);
        setTosses(tosses + 1);
        playTones({
          tones: next === 6 ? [...DIE_TICKS, ...SIX_CHIME] : DIE_TICKS,
          wave: 'triangle',
        });
      }}
    >
      <svg
        viewBox="0 0 100 64"
        width={size}
        height={size * 0.64}
        overflow="visible"
        aria-hidden
      >
        <defs>
          <FeltDefs ids={ids} />
        </defs>
        <ellipse
          cx={50}
          cy={46}
          rx={48}
          ry={15}
          fill={WATER_LIGHT}
          filter={`url(#${ids.fuzz})`}
        />
        <ellipse cx={85} cy={42} rx={8} ry={3} fill={OLLIE.dark} />
        <ellipse cx={56} cy={40} rx={23} ry={8} fill={OLLIE.fur} />
        <ellipse cx={58} cy={37.4} rx={15} ry={4.2} fill={OLLIE.belly} />
        <ellipse
          cx={77}
          cy={32.6}
          rx={2.6}
          ry={4}
          fill={OLLIE.dark}
          transform="rotate(-25 77 32.6)"
        />
        <ellipse
          cx={81.5}
          cy={34}
          rx={2.6}
          ry={4}
          fill={OLLIE.dark}
          transform="rotate(-10 81.5 34)"
        />
        <g transform="translate(31 34) rotate(-14) scale(0.95)">
          <OllieHead mood={tosses === 0 ? 'calm' : isSix ? 'happy' : 'calm'} />
        </g>
        <g key={tosses}>
          <Toss>
            <g transform="translate(57 30.6) scale(1.15)">
              <LuckyDie face={face} />
            </g>
          </Toss>
        </g>
        <ellipse cx={51.5} cy={33.8} rx={2.2} ry={1.6} fill={OLLIE.dark} />
        <ellipse cx={62.5} cy={33.8} rx={2.2} ry={1.6} fill={OLLIE.dark} />
        <WaterFront y={42} />
        <path
          d="M 18 50 q 4 -2 8 0 M 70 53 q 4 -2 8 0 M 44 56 q 4 -2 8 0"
          stroke="#FFFFFF"
          strokeWidth={0.9}
          fill="none"
          strokeLinecap="round"
          opacity={0.8}
        />
        {isSix && (
          <Hop key={`six-${tosses}`}>
            <Sparkle x={46} y={20} r={3} />
            <Sparkle x={68} y={17} r={2.4} />
          </Hop>
        )}
      </svg>
    </ToyShell>
  );
}

/* ── Holding paws ─────────────────────────────────────────────────────── */

const drift = keyframes`
  0% { transform: translate(0, 0); opacity: 0; }
  20% { opacity: 1; }
  100% { transform: translate(4px, -12px); opacity: 0; }
`;

/** A snore z drifting up and fading. */
const Snore = styled.g<{$delaySec: number}>`
  opacity: 0;
  animation: ${drift} 2.4s ease-out ${({$delaySec}) => $delaySec}s infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
  }
`;

/** A friend otter's lighter fur. */
const FRIEND_FUR = OLLIE.furLight;

/**
 * One otter floating on its back, seen from above, head up: about 22
 * units wide and 40 tall, centered on its tummy.
 */
function FloatingOtter({fur, isAwake}: {fur: string; isAwake: boolean}) {
  return (
    <g>
      <ellipse cx={0} cy={18} rx={4} ry={6} fill={OLLIE.dark} />
      <ellipse cx={0} cy={3} rx={10} ry={14} fill={fur} />
      <ellipse cx={0} cy={4} rx={6.6} ry={9.6} fill={OLLIE.belly} />
      <g transform="translate(0 -15)">
        <OllieHead mood={isAwake ? 'happy' : 'asleep'} fur={fur} />
      </g>
    </g>
  );
}

/**
 * Ollie and a friend napping on the water, holding paws so they don't
 * drift apart, as sea otters do. Each press wakes them (a heart pops
 * between them) or lets them doze off again.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToyOttersHoldingPaws({size, isPreview = false}: MapToyProps) {
  const ids = useFeltIds();
  const [presses, setPresses] = useState(0);
  const isAwake = presses % 2 === 1;

  return (
    <ToyShell
      isPreview={isPreview}
      label="Otters holding paws"
      caption={
        isAwake
          ? 'Ollie and their friend wake up, still holding paws'
          : 'Ollie and a friend nap on the water, holding paws so they don’t drift apart'
      }
      onPoke={() => {
        setPresses(presses + 1);
        playTones({
          tones: isAwake
            ? [
                {frequency: 659.3, startSec: 0, durationSec: 0.4},
                {frequency: 523.3, startSec: 0.25, durationSec: 0.6},
              ]
            : [
                {frequency: 523.3, startSec: 0, durationSec: 0.15},
                {frequency: 784, startSec: 0.12, durationSec: 0.3},
              ],
        });
      }}
    >
      <svg
        viewBox="0 0 110 72"
        width={size}
        height={size * (72 / 110)}
        overflow="visible"
        aria-hidden
      >
        <defs>
          <FeltDefs ids={ids} />
        </defs>
        <ellipse
          cx={55}
          cy={44}
          rx={53}
          ry={24}
          fill={WATER_LIGHT}
          filter={`url(#${ids.fuzz})`}
        />
        {[
          {x: 14, y: 34},
          {x: 92, y: 52},
          {x: 22, y: 60},
        ].map(({x, y}) => (
          <path
            key={x}
            d={`M ${x} ${y} q 4 -2 8 0`}
            stroke="#FFFFFF"
            strokeWidth={0.9}
            fill="none"
            strokeLinecap="round"
            opacity={0.8}
          />
        ))}
        <g transform="translate(38 42) rotate(-8)">
          <FloatingOtter fur={OLLIE.fur} isAwake={isAwake} />
        </g>
        <g transform="translate(72 44) rotate(8)">
          <FloatingOtter fur={FRIEND_FUR} isAwake={isAwake} />
        </g>
        <path
          d="M 45 40 Q 55 46 65 41"
          stroke={OLLIE.dark}
          strokeWidth={3.2}
          fill="none"
          strokeLinecap="round"
        />
        <circle cx={55} cy={43.6} r={2.6} fill={OLLIE.dark} />
        {isAwake ? (
          <Hop key={`heart-${presses}`}>
            <path
              d="M 55 32 C 50 28 51 23 54 23 C 55 23 55 24 55 25 C 55 24 55 23 56 23 C 59 23 60 28 55 32 Z"
              fill={FELT.pink}
            />
          </Hop>
        ) : (
          [
            {x: 40, delaySec: 0},
            {x: 76, delaySec: 1.2},
          ].map(({x, delaySec}) => (
            <Snore key={x} $delaySec={delaySec}>
              <path
                d={`M ${x} 14 h 3 l -3 3 h 3`}
                stroke={FELT.ink}
                strokeWidth={0.8}
                fill="none"
                strokeLinejoin="round"
                opacity={0.7}
              />
            </Snore>
          ))
        )}
      </svg>
    </ToyShell>
  );
}

/* ── Juggling Ollie ───────────────────────────────────────────────────── */

/** Chance a juggle ends with a pebble getting away. */
const DROP_CHANCE = 1 / 5;

/** Where the pebbles rest, beside Ollie, in the overlay's 100×130 units. */
const PILE = [
  {x: 84, y: 124},
  {x: 92, y: 125},
  {x: 88, y: 119},
] as const;

/** Where a dropped pebble lands, on Ollie's other side. */
const DROPPED_AT = {x: 12, y: 125};

/** The juggling loop, above Ollie's head: one hand, the top, the other. */
const LOOP = [
  {x: 30, y: 48},
  {x: 50, y: 6},
  {x: 70, y: 48},
  {x: 50, y: 56},
] as const;

/**
 * Keyframes for one pebble: from the pile, two times round the loop
 * starting a third of the way along for each pebble, then to where it
 * rests.
 */
function juggleFrames({
  pebble,
  end,
}: {
  pebble: number;
  end: {x: number; y: number};
}) {
  const start = PILE[pebble] ?? PILE[0];
  const loopPoints = Array.from({length: 9}, (_, index) => {
    const point = LOOP[(index + pebble) % LOOP.length] ?? LOOP[0];
    return `${8 + index * 10}% { transform: translate(${point.x}px, ${point.y}px); }`;
  }).join('\n');
  return keyframes`
    0% { transform: translate(${start.x}px, ${start.y}px); }
    ${loopPoints}
    100% { transform: translate(${end.x}px, ${end.y}px); }
  `;
}

/** A pebble in flight; its keyframes come from {@link juggleFrames}. */
const Pebble = styled.g<{$frames: ReturnType<typeof keyframes>}>`
  animation: ${({$frames}) => $frames} 2.4s ease-in-out 1 forwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const PEBBLE_COLORS = ['#9AA5B1', '#C9B48A', '#7FA7A0'] as const;

const JuggleBox = styled.div<{$size: number}>`
  position: relative;
  width: ${({$size}) => $size}px;
  height: ${({$size}) => $size * 1.3}px;
`;

const PlushSeat = styled.div<{$size: number}>`
  position: absolute;
  bottom: 0;
  left: ${({$size}) => $size * 0.12}px;
`;

/**
 * Ollie juggling three pebbles, as otters really do with stones. Each press
 * starts a juggle; now and then a pebble gets away.
 *
 * @param props - See {@link MapToyProps}.
 * @return The toy.
 * @throws Never.
 */
export function ToyOllieJuggling({size, isPreview = false}: MapToyProps) {
  const [juggles, setJuggles] = useState(0);
  const [droppedPebble, setDroppedPebble] = useState<number | undefined>();
  const isDropped = droppedPebble !== undefined;

  return (
    <ToyShell
      isPreview={isPreview}
      label="Ollie juggling"
      caption={
        juggles === 0
          ? 'Ollie sits by a pile of pebbles'
          : isDropped
            ? 'Ollie juggles three pebbles. Oops, one got away'
            : 'Ollie juggles three pebbles. Otters really do juggle stones'
      }
      onPoke={() => {
        const willDrop = Math.random() < DROP_CHANCE;
        setDroppedPebble(
          willDrop ? Math.floor(Math.random() * PILE.length) : undefined,
        );
        setJuggles(juggles + 1);
        playTones({
          tones: [0, 0.4, 0.8, 1.2, 1.6].map(startSec => ({
            frequency: 880,
            startSec,
            durationSec: 0.05,
          })),
          wave: 'triangle',
        });
      }}
    >
      <JuggleBox $size={size}>
        <PlushSeat $size={size}>
          <OlliePlush
            size={size * 0.76}
            state={juggles === 0 ? 'idle' : isDropped ? 'surprised' : 'happy'}
          />
        </PlushSeat>
        <svg
          viewBox="0 0 100 130"
          width={size}
          height={size * 1.3}
          overflow="visible"
          style={{position: 'absolute', inset: 0}}
          aria-hidden
        >
          {PEBBLE_COLORS.map((color, pebble) => {
            const rest =
              droppedPebble === pebble ? DROPPED_AT : (PILE[pebble] ?? PILE[0]);
            const pebbleShape = (
              <ellipse cx={0} cy={0} rx={4.2} ry={3.2} fill={color} />
            );
            return juggles === 0 || isPreview ? (
              <g key={color} transform={`translate(${rest.x} ${rest.y})`}>
                {pebbleShape}
              </g>
            ) : (
              <Pebble
                key={`${color}-${juggles}`}
                $frames={juggleFrames({pebble, end: rest})}
                style={{transform: `translate(${rest.x}px, ${rest.y}px)`}}
              >
                {pebbleShape}
              </Pebble>
            );
          })}
        </svg>
      </JuggleBox>
    </ToyShell>
  );
}
